import { Avatar, BottomSheet, Button, Typography } from '@/ui-kit';
import styles from './message-chat.module.scss';
import { Chat } from '@/types/chat';
import React, { useState } from 'react';
import { generatePath } from 'react-router-dom';
import { routesApp } from '@/constans/routes';
import { differenceInYearsResult } from '@/utils/difference-in-years-result';
import { format } from 'date-fns';
import { useNavigate } from 'react-router';
import { CheckMessageIcon, NoCheckMessageIcon, PremiumIcon, VerifiedAccountIcon } from '@/assets/icons';
import clsx from 'clsx';
import WebApp from '@twa-dev/sdk';
import { useVibration } from '@/store/useVibration';
import { ChatViewModel } from '@/types/vm/chat';
import { useAuth } from '@/store/useAuth';
import { PremiumBottomsheet } from '../premium-bottomsheet';
import { getProfileNameAndAge } from '@/utils/msgs';
import { useTranslation } from 'react-i18next';

interface Props {
    chat: Chat;
}

export const MessageChat: React.FC<Props> = ({ chat }) => {
    const userData =
        useAuth((state) => state.getUser());

    const isNewMessage = !chat.message?.is_read;
    const isMineMessage = chat.message?.profile_id === userData?.id;
    const unreadMessagesCount = chat.unread_count ?? 0;

    const isShowMessageCounter = isNewMessage && !!unreadMessagesCount && !isMineMessage;

    const navigate = useNavigate();

    const { isVibrationEnabled } =
        useVibration(state => state);

    const toUserProfile = () => {
        navigate(generatePath(routesApp.profileView, { id: String(chat.profile.id) }));
        if (isVibrationEnabled) {
            WebApp
                .HapticFeedback
                .impactOccurred('medium');
            // pass for not android
            navigator.vibrate?.(10)
        }
    };


    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);

    const [isPremiumModalOpen, setIsPremiumModalOpen] =
        useState(false);

    const chatViewModel =
        new ChatViewModel(chat)

    const { t } = useTranslation();

    return (
        <div
            className={
                [
                    styles.messageChat,
                    'messageChat'
                ].join(' ')
            }>
            <div
                className={
                    clsx(
                        styles.avatar,
                        chat.profile.is_online && styles['avatar--online']
                    )
                }
                onClick={toUserProfile}>
                <Avatar
                    size={60}
                    src={chat.profile?.photos[0]?.thumb_url}
                    alt={chat.profile?.public_name ?? 'avatar'} />
                {/* {chat.status === 'online' && <span className={styles.status}></span>} */}
            </div>
            <div className={styles.wrapInfo}>
                <div
                    className={styles.link}
                    onClick={() => navigate(generatePath(routesApp.chat, { id: String(chat.chat_id) }))}>
                    <div className={styles.info}>
                        <div className={styles.nameAndVerificationStatusInfo}>
                            <Typography
                                typeColor="brown"
                                className={styles.name}>
                                {getProfileNameAndAge(chat.profile)}
                            </Typography>
                            {
                                chat
                                    .profile
                                    ?.verification_status
                                    === 'verified' &&
                                <VerifiedAccountIcon
                                    width={20}
                                    height={20}
                                    className={styles.verificationStatusInfo}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsVerificationModalOpen(true)
                                    }} />
                            }
                            {
                                chatViewModel.isPremium() &&
                                <PremiumIcon
                                    width={20}
                                    height={20}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsPremiumModalOpen(true)
                                    }} />
                            }
                        </div>
                        <div className={styles.chatAside}>
                            {
                                chat.message?.profile_id === userData?.id && (
                                    chat.message?.is_read ?
                                        <CheckMessageIcon
                                            width={12}
                                            height={12} />
                                    :
                                        <NoCheckMessageIcon width={12} height={12} />
                                )
                            }
                            {chat.message?.created_at && (
                                <Typography typeColor="gray" className={styles.time}>
                                    {format(new Date(chat.message.created_at), 'HH:mm')}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <div className={styles.messageInfo}>
                        <Typography
                            className={styles.lastMessage}
                            typeColor={isNewMessage && !isMineMessage ? 'orange' : 'gray-light'}>
                            {chat.message?.text ? chat.message.text : ''}
                        </Typography>
                        {isShowMessageCounter && (
                            <div className={styles.countMessage}>
                                <Typography
                                    variant="span"
                                    typeColor="white"
                                    className={styles.count}>
                                    {chat.unread_count}
                                </Typography>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <BottomSheet
                overlayClassName={styles.bottomsheetOverlay}
                isOpen={isVerificationModalOpen}
                isDeskorTop={false}
                onClose={() => setIsVerificationModalOpen(false)}>
                <div className={styles.verificationModalContent}>
                    <div className={styles.verificationModalContentIcon}>
                        <Avatar
                            src={chatViewModel.mainPhotoUrl}
                            size={96} />
                        <VerifiedAccountIcon
                            className={styles.verificationModalContentIconVerification}
                            width={28}
                            height={28} />
                    </div>
                    <div className={styles.verificationModalContentHeader}>
                        <Typography
                            className={styles.verificationModalContentHeaderTitle}
                            variant={'h5'}
                            fontWeight={'bold'}>
                            {chatViewModel.successVerificationTitle}
                        </Typography>
                        <Typography
                            className={styles.verificationModalContentHeaderDesc}>
                            {t('verification_desc')}
                        </Typography>
                    </div>
                    <div className={styles.verificationModalContentFootnote}>
                        <Button
                            typeColor="orange"
                            className={styles.verificationModalContentFootnoteBtn}
                            onClick={() => setIsVerificationModalOpen(false)}>
                            {t('ok_tertiary')}
                        </Button>
                    </div>
                </div>
            </BottomSheet>
            <PremiumBottomsheet
                photo={chat.profile.photos.length ? chat.profile.photos[0].main_url : null}
                name={chat.profile.public_name}
                isPremiumModalOpen={isPremiumModalOpen}
                setIsPremiumModalOpen={setIsPremiumModalOpen} />
        </div>
    );
};
